"use client";

import { useState } from "react";
import { format } from "date-fns";
import Link from "next/link";
import { Plus, Search, MoreHorizontal, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { mockPosts, platformIcons, statusConfig } from "@/lib/mock-data";
import { useClient } from "@/lib/client-context";

export default function PostsPage() {
  const { selectedClientId } = useClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");

  const filteredPosts = mockPosts.filter((post) => {
    const matchesClient = selectedClientId === "all" || post.clientId === selectedClientId;
    const matchesSearch = post.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || post.status === statusFilter;
    const matchesPlatform = platformFilter === "all" || post.platform === platformFilter;
    return matchesClient && matchesSearch && matchesStatus && matchesPlatform;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Posts</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {filteredPosts.length} total posts &middot; {filteredPosts.filter(p => p.status === "SCHEDULED").length} scheduled
          </p>
        </div>
        <Button className="shadow-sm shadow-primary/25">
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 px-8 py-4 border-b bg-card/50">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => v && setStatusFilter(v)}>
          <SelectTrigger className="w-[150px] h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="PENDING_APPROVAL">Pending</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="SCHEDULED">Scheduled</SelectItem>
            <SelectItem value="PUBLISHED">Published</SelectItem>
          </SelectContent>
        </Select>
        <Select value={platformFilter} onValueChange={(v) => v && setPlatformFilter(v)}>
          <SelectTrigger className="w-[150px] h-9">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            <SelectItem value="INSTAGRAM">Instagram</SelectItem>
            <SelectItem value="FACEBOOK">Facebook</SelectItem>
            <SelectItem value="TWITTER">Twitter/X</SelectItem>
            <SelectItem value="LINKEDIN">LinkedIn</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      <div className="flex-1 p-8 overflow-auto">
        <Tabs defaultValue="list">
          <TabsList className="mb-6">
            <TabsTrigger value="list">List</TabsTrigger>
            <TabsTrigger value="grid">Grid</TabsTrigger>
          </TabsList>

          <TabsContent value="list">
            <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_120px_120px_120px_140px_50px] gap-4 px-5 py-3 border-b bg-muted/30 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <span>Content</span>
                <span>Platform</span>
                <span>Status</span>
                <span>Author</span>
                <span>Date</span>
                <span></span>
              </div>

              {filteredPosts.map((post) => {
                const platform = platformIcons[post.platform];
                const status = statusConfig[post.status];
                return (
                  <Link
                    key={post.id}
                    href={`/content/posts/${post.id}`}
                    className="grid grid-cols-[1fr_120px_120px_120px_140px_50px] gap-4 px-5 py-4 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer items-center"
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      {post.mediaUrl && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={post.mediaUrl} alt="" className="h-10 w-10 rounded-md object-cover shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{post.content}</p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        {post.labels.map((label) => (
                          <span
                            key={label.name}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: label.color }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                      </div>
                    </div>
                    <span className={cn("text-xs font-medium px-2 py-1 rounded-md w-fit", platform?.bg, platform?.color)}>
                      {platform?.label}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className={cn("h-2 w-2 rounded-full", status.dot)} />
                      <span className={cn("text-xs font-medium", status.color)}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                          {post.authorInitials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-muted-foreground">{post.author.split(" ")[0]}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(post.scheduledAt, "MMM d, h:mm a")}
                    </span>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.preventDefault()}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </Link>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="grid">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPosts.map((post) => {
                const platform = platformIcons[post.platform];
                const status = statusConfig[post.status];
                return (
                  <Link key={post.id} href={`/content/posts/${post.id}`} className="block">
                  <Card className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden">
                    {post.mediaUrl && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={post.mediaUrl} alt="" className="w-full h-40 object-cover" />
                    )}
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className={cn("text-xs font-medium px-2 py-1 rounded-md", platform?.bg, platform?.color)}>
                          {platform?.label}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className={cn("h-2 w-2 rounded-full", status.dot)} />
                          <span className={cn("text-xs font-medium", status.color)}>{status.label}</span>
                        </div>
                      </div>
                      <p className="text-sm line-clamp-3 mb-3">{post.content}</p>
                      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                        {post.labels.map((label) => (
                          <span
                            key={label.name}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: label.color }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                              {post.authorInitials}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-muted-foreground">{post.author}</span>
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          {format(post.scheduledAt, "MMM d")}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  </Link>
                );
              })}

              <Card className="border-dashed border-2 cursor-pointer hover:bg-accent/50 hover:border-primary/30 transition-all duration-150 group">
                <CardContent className="flex items-center justify-center h-52">
                  <div className="text-center text-muted-foreground group-hover:text-primary transition-colors">
                    <div className="h-12 w-12 rounded-xl bg-muted group-hover:bg-primary/10 flex items-center justify-center mx-auto mb-3 transition-colors">
                      <Plus className="h-6 w-6" />
                    </div>
                    <p className="text-sm font-medium">Create New Post</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
